import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { apiError, apiFieldErrors, todayIso } from './paymentUtils';

const EMPTY = { paid_at: '', reference: '', amount: '', notes: '' };

/**
 * Formulario de pago (sin comprobante). Lo usan el proveedor (reportar) y el superadmin (registrar).
 * `onSubmit(values)` debe devolver una promesa; si falla se muestra el error del servidor.
 */
export default function PaymentFormModal({ show, onHide, title, subtitle, submitLabel, onSubmit, children, extra }) {
    const [values, setValues] = useState(EMPTY);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});

    useEffect(() => {
        if (show) {
            setValues({ ...EMPTY, paid_at: todayIso() });
            setError('');
            setFieldErrors({});
            setSubmitting(false);
        }
    }, [show]);

    const set = (key) => (e) => setValues((v) => ({ ...v, [key]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        setError('');
        setFieldErrors({});
        try {
            await onSubmit({
                paid_at: values.paid_at,
                reference: values.reference.trim(),
                amount: values.amount === '' ? null : Number(values.amount),
                notes: values.notes.trim() || null,
            });
        } catch (err) {
            setError(apiError(err));
            setFieldErrors(apiFieldErrors(err));
            setSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={submitting ? undefined : onHide} centered>
            <Form onSubmit={handleSubmit}>
                <Modal.Header closeButton={!submitting}>
                    <Modal.Title style={{ fontSize: 18 }}>{title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {subtitle && <p style={{ fontSize: 13, color: 'var(--abi-muted)' }}>{subtitle}</p>}
                    {error && <Alert variant="danger" className="py-2">{error}</Alert>}

                    {children}

                    <Form.Group className="mb-3">
                        <Form.Label>Fecha de pago</Form.Label>
                        <Form.Control type="date" required max={todayIso()} value={values.paid_at}
                            onChange={set('paid_at')} isInvalid={!!fieldErrors.paid_at} />
                        <Form.Control.Feedback type="invalid">{fieldErrors.paid_at}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Referencia / número de transacción</Form.Label>
                        <Form.Control required maxLength={120} value={values.reference}
                            onChange={set('reference')} isInvalid={!!fieldErrors.reference} />
                        <Form.Control.Feedback type="invalid">{fieldErrors.reference}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Valor pagado (opcional)</Form.Label>
                        <Form.Control type="number" min="0" step="0.01" value={values.amount}
                            onChange={set('amount')} isInvalid={!!fieldErrors.amount} />
                        <Form.Control.Feedback type="invalid">{fieldErrors.amount}</Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group>
                        <Form.Label>Notas (opcional)</Form.Label>
                        <Form.Control as="textarea" rows={2} maxLength={1000} value={values.notes}
                            onChange={set('notes')} isInvalid={!!fieldErrors.notes} />
                        <Form.Control.Feedback type="invalid">{fieldErrors.notes}</Form.Control.Feedback>
                    </Form.Group>

                    {extra}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={onHide} disabled={submitting}>Cancelar</Button>
                    <Button type="submit" variant="primary" disabled={submitting || !values.paid_at || !values.reference.trim()}>
                        {submitting ? 'Enviando…' : submitLabel}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}
